import React from 'react'
import { ThemeProvider } from '@emotion/react'
import { NameDetails } from '@mobi/api-types'
import { TabButton, Tabs } from '@mobi/component-library/Navigation/Tabs'
import { PageHeader } from '@mobi/component-library/Navigation/PageHeader'
import { MainContainer } from '@core/Components/Containers'
import { Paragraph } from '@core/Components/Text/Paragraph'
import { DepositLimitsContainerStyled as DepositLimitsContainer } from '../DepositLimits/DepositLimits.styles'
import { AccountHolders, AccountHolder } from '@core/Data/Account/accountHolder'
import { DepositLimitsViewAccountHolder } from '../DepositLimitsViewAccountHolder/DepositLimitsViewAccountHolder'
import { getDepositLimitTheme, DepositLimitThemes } from '../../themes'

interface DepositLimitsViewProps {
  accountHolders: AccountHolders
  selectedAccountHolderNumber?: number
  isDesktop?: boolean
  saveSuccess?: boolean
}

// eslint-disable-next-line react/prefer-stateless-function
export class DepositLimitsView extends React.Component<DepositLimitsViewProps> {
  public render(): JSX.Element {
    const {
      accountHolders: { primaryAccountHolder, secondaryAccountHolder },
      selectedAccountHolderNumber,
      isDesktop,
      saveSuccess,
    } = this.props

    return (
      <ThemeProvider theme={getDepositLimitTheme(DepositLimitThemes.default)}>
        <MainContainer testId='deposit-limits-container'>
          {!isDesktop && <PageHeader title='Deposit Limits' />}

          <DepositLimitsContainer>
            <Paragraph>
              TABtouch supports responsible wagering. Manage the amount you can deposit by setting
              yourself weekly, fortnightly or even monthly limits to help you manage your betting.
            </Paragraph>

            {!secondaryAccountHolder ? (
              <DepositLimitsViewAccountHolder
                isDesktop={isDesktop}
                accountHolder={primaryAccountHolder}
                saveSuccess={saveSuccess}
              />
            ) : (
              <DepositLimitsViewAccountHolderTabs
                isDesktop={isDesktop}
                primaryAccountHolder={primaryAccountHolder}
                secondaryAccountHolder={secondaryAccountHolder}
                selectedAccountHolderNumber={selectedAccountHolderNumber}
                saveSuccess={saveSuccess}
              />
            )}
          </DepositLimitsContainer>
        </MainContainer>
      </ThemeProvider>
    )
  }
}

interface DepositLimitsViewAccountHolderTabsProps {
  primaryAccountHolder: AccountHolder
  secondaryAccountHolder: AccountHolder
  isDesktop?: boolean
  selectedAccountHolderNumber?: number
  saveSuccess?: boolean
}

export const DepositLimitsViewAccountHolderTabs = ({
  primaryAccountHolder,
  secondaryAccountHolder,
  selectedAccountHolderNumber,
  saveSuccess,
  isDesktop,
}: DepositLimitsViewAccountHolderTabsProps): JSX.Element => {
  const [currentTab, setCurrentTab] = React.useState<TabNavs>(() =>
    secondaryAccountHolder.BetAccountHolderNumber === selectedAccountHolderNumber
      ? 'Secondary'
      : 'Primary'
  )

  return (
    <>
      <Tabs>
        {[primaryAccountHolder, secondaryAccountHolder].map((accountHolder, index) => {
          const tabLabel = buildName(accountHolder.NameDetails)
          const tabName: TabNavs = index === 0 ? 'Primary' : 'Secondary'
          return (
            <TabButton
              key={tabName}
              content={tabLabel}
              isSelected={currentTab === tabName}
              testId={`DepositLimitTab${tabName}`}
              onClick={() => setCurrentTab(tabName)}
            />
          )
        })}
      </Tabs>

      {currentTab === 'Primary' && (
        <DepositLimitsViewAccountHolder
          isDesktop={isDesktop}
          accountHolder={primaryAccountHolder}
          saveSuccess={
            saveSuccess &&
            selectedAccountHolderNumber === primaryAccountHolder.BetAccountHolderNumber
          }
        />
      )}

      {currentTab === 'Secondary' && (
        <DepositLimitsViewAccountHolder
          isDesktop={isDesktop}
          accountHolder={secondaryAccountHolder}
          saveSuccess={
            saveSuccess &&
            selectedAccountHolderNumber === secondaryAccountHolder.BetAccountHolderNumber
          }
        />
      )}
    </>
  )
}

function buildName(nameDetails: NameDetails) {
  return [nameDetails.FirstName, nameDetails.Surname].filter(n => !!n).join(' ')
}

// =====
// Types
// =====

type TabNavs = 'Primary' | 'Secondary'
